<script setup>
// O cartão de documento do kit Polaris falando a língua desta tela.
//
// O cartão portado (cards/DocumentCard.vue) é o do kit e fica igual a ele; este
// adaptador traduz o documento que vem da API (datas em epoch, status de
// sincronização, contagem) para o que o cartão mostra, e devolve os eventos no
// formato que a página já entende ({ action, id }).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ptBR } from 'date-fns/locale';
import { dynamicTime } from 'shared/helpers/timeHelper';
import DocumentCard from 'dashboard/components-next/polaris/cards/DocumentCard.vue';

const props = defineProps({
  polarisDocument: { type: Object, required: true },
  selectable: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['action', 'select']);

const { t, locale } = useI18n();

// O date-fns fala inglês quando não recebe um locale, e a tela ficava
// "última atualização em about 3 hours ago". Aqui ela fala a língua da pessoa.
const dateLocale = computed(() =>
  locale.value === 'pt_BR' ? ptBR : undefined
);

const doc = computed(() => props.polarisDocument);

// O kit diz o estado em palavras, com três valores. O app tem mais nuances de
// sincronização; elas caem no estado que descreve o que a pessoa precisa saber:
// dá para usar este documento agora, ainda não, ou não deu para ler.
const status = computed(() => {
  if (doc.value.sync_in_progress) return 'processing';
  if (doc.value.last_sync_error_code || doc.value.status === 'failed') {
    return 'failed';
  }
  return 'available';
});

// As mesmas palavras que esta tela ja usava (POLARIS.DOCUMENTS.SYNC_STATUS):
// "atualizando...", "ultima atualizacao em X", "ainda nao atualizado". Um estado
// nao pode ter dois nomes na mesma tela.
// Quando falha, a tela sempre disse POR QUE falhou ("Página não encontrada",
// "Acesso negado"), e não só que falhou. Isso se mantém.
const SYNC_ERRORS = {
  not_found: 'POLARIS.DOCUMENTS.SYNC_ERRORS.NOT_FOUND',
  access_denied: 'POLARIS.DOCUMENTS.SYNC_ERRORS.ACCESS_DENIED',
  timeout: 'POLARIS.DOCUMENTS.SYNC_ERRORS.TIMEOUT',
  content_empty: 'POLARIS.DOCUMENTS.SYNC_ERRORS.CONTENT_EMPTY',
  fetch_failed: 'POLARIS.DOCUMENTS.SYNC_ERRORS.FETCH_FAILED',
  sync_error: 'POLARIS.DOCUMENTS.SYNC_ERRORS.SYNC_ERROR',
};

const synced = computed(() => {
  if (doc.value.sync_in_progress) {
    return t('POLARIS.DOCUMENTS.SYNC_STATUS.SYNCING');
  }
  if (doc.value.last_sync_error_code) {
    return t(
      SYNC_ERRORS[doc.value.last_sync_error_code] ||
        'POLARIS.DOCUMENTS.SYNC_ERRORS.DEFAULT'
    );
  }
  if (!doc.value.last_synced_at) {
    return t('POLARIS.DOCUMENTS.SYNC_STATUS.NEVER_SYNCED');
  }
  return t('POLARIS.DOCUMENTS.SYNC_STATUS.SYNCED', {
    time: dynamicTime(doc.value.last_synced_at, dateLocale.value),
  });
});

const source = computed(() =>
  doc.value.pdf_document ? doc.value.name : doc.value.external_link
);

// O menu do kit tem "abrir a página", que só faz sentido para um documento que
// veio de uma URL; os detalhes abrem clicando no cartão, como já era aqui.
const onAction = action => {
  if (action === 'open') {
    window.open(doc.value.external_link, '_blank', 'noopener');
    return;
  }
  emit('action', { action, id: doc.value.id });
};

// O clique no cartão abre os detalhes, menos quando foi no menu ou no seletor.
const onClick = event => {
  if (event.target.closest('[data-slot="item-actions"], label')) return;
  emit('action', { action: 'viewDetails', id: doc.value.id });
};
</script>

<template>
  <div @click="onClick">
    <DocumentCard
      :name="doc.name || doc.external_link"
      :source="source"
      :pdf="doc.pdf_document"
      :status="status"
      :synced="synced"
      :responses="doc.responses_count || 0"
      :selectable="selectable"
      :selected="selected"
      @action="({ action }) => onAction(action)"
      @select="emit('select', doc.id)"
    />
  </div>
</template>
